import React, {useEffect, useState} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {applyPageSEO} from '../utils/seo';

const {FiAlertCircle, FiCalendar, FiCheck, FiExternalLink, FiMapPin, FiPhone} = FiIcons;
const MAP_LINK = 'https://maps.app.goo.gl/jFsf5zKT3sU3UdDJA';
const services = [
  {id: 'pre-test', tab: 'Pre-Test Preparation', title: 'Pre-Test Preparation Package', subtitle: 'Focused preparation for test-ready drivers', price: '$140', period: '1 hour', bookingUrl: 'https://tidycal.com/drivepal/mock', requirements: ['For test-ready drivers preparing for an upcoming driving test', 'Independent mock driving assessment and focused feedback', 'Uses your own registered and roadworthy automatic vehicle', 'Meet near the Service NSW Goulburn test centre'], warning: 'This service is not suitable for beginner drivers. Your instructor may refuse or end a session if the student, vehicle or driving conditions are unsafe.', showMap: true},
  {id: 'single-lesson', tab: '1 Hour Lesson', title: 'Single Lesson', subtitle: 'Foundational skills and confidence building', price: '$95', period: '1 hour', bookingUrl: 'https://tidycal.com/drivepal/60', requirements: ['Structured automatic driving instruction', 'Check eligibility and availability before booking', 'Coaching customised to your current skill level', 'Calm, supportive and goal-driven guidance'], warning: 'Your Drive Pal instructor will confirm eligibility, meeting point, vehicle arrangements and lesson focus before the session. Santosh is currently taking test-ready students only.', showMap: false},
  {id: 'test-day-hire', tab: 'Test Day Car Hire', title: 'Test Day Car Hire', subtitle: 'Test-day preparation and vehicle arrangements', price: '$250', period: 'package', bookingUrl: 'https://tidycal.com/drivepal/carhire', requirements: ['Discuss vehicle availability before booking', 'Pre-test warm-up and preparation', 'Confirm test-day arrangements with Drive Pal', 'Focused support before your assessment'], warning: 'Vehicle arrangements are subject to confirmation. A driving-test result cannot be guaranteed; outcomes are determined solely by Transport for NSW.', showMap: true}
];

function ServiceTabs({activeService, onChange}) {
  return (
    <div className="mx-auto mb-8 grid max-w-4xl grid-cols-1 gap-2 rounded-2xl border border-white/10 bg-brand-navy/60 p-2 sm:grid-cols-3" role="tablist" aria-label="Choose a booking service">
      {services.map((service) => (
        <button key={service.id} type="button" role="tab" aria-selected={activeService === service.id} aria-controls={`${service.id}-booking-panel`} onClick={() => onChange(service.id)} className={`rounded-xl px-4 py-3 text-sm font-bold transition-all sm:text-base ${activeService === service.id ? 'bg-brand-gold text-brand-navy shadow-lg' : 'text-white/75 hover:bg-white/10 hover:text-white'}`}>
          {service.tab}
        </button>
      ))}
    </div>
  );
}

function ServiceDetails({service}) {
  return (
    <div className="mb-8 text-white/80">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="mb-2 text-sm font-bold uppercase tracking-wider text-brand-gold">{service.subtitle}</p>
          <h2 className="font-display text-2xl text-white md:text-3xl">{service.title}</h2>
        </div>
        <span className="rounded-full bg-brand-gold px-5 py-2 font-display text-lg font-bold text-brand-navy">{service.price} / {service.period}</span>
      </div>
      <div className="mb-5 grid gap-3 sm:grid-cols-2">
        {service.requirements.map((requirement) => (
          <div key={requirement} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/5 p-4">
            <SafeIcon icon={FiCheck} className="mt-0.5 shrink-0 text-lg text-brand-green" />
            <p className="text-sm leading-relaxed text-white/85">{requirement}</p>
          </div>
        ))}
      </div>
      <div className="flex items-start gap-3 rounded-xl border border-brand-gold/30 bg-brand-gold/10 p-4">
        <SafeIcon icon={FiAlertCircle} className="mt-0.5 shrink-0 text-xl text-brand-gold" />
        <p className="text-sm leading-relaxed text-white/85">{service.warning}</p>
      </div>
      {service.showMap && (
        <a href={MAP_LINK} target="_blank" rel="noopener noreferrer" className="group mt-5 flex items-start gap-3 rounded-xl border border-brand-gold/20 bg-brand-gold/10 p-4 transition-colors hover:bg-brand-gold/20">
          <SafeIcon icon={FiMapPin} className="mt-0.5 shrink-0 text-2xl text-brand-gold" />
          <div>
            <p className="font-medium text-white/90">View the Service NSW Goulburn test centre meeting area.</p>
            <span className="mt-1 inline-block text-sm font-semibold text-brand-gold">Open in Google Maps →</span>
          </div>
        </a>
      )}
    </div>
  );
}

function BookingPanel({service}) {
  return (
    <motion.div id={`${service.id}-booking-panel`} key={service.id} role="tabpanel" initial={{opacity: 0, y: 16}} animate={{opacity: 1, y: 0}} transition={{duration: 0.25}} className="mx-auto max-w-[1000px] overflow-hidden rounded-2xl border-2 border-brand-gold bg-brand-surface shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
      <div className="p-4 md:p-8">
        <ServiceDetails service={service} />
        <div className="overflow-hidden rounded-xl bg-white p-1 shadow-inner md:p-2">
          <iframe key={service.id} src={service.bookingUrl} title={`${service.title} Booking Calendar`} className="min-h-[680px] w-full rounded-lg border-0 bg-white md:min-h-[760px]" loading="lazy" allow="payment;fullscreen;clipboard-write" />
        </div>
        <div className="mt-6 flex flex-col items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 p-4 text-center sm:flex-row sm:text-left">
          <p className="text-sm text-white/70">If the calendar does not appear, open this service directly in TidyCal.</p>
          <a href={service.bookingUrl} target="_blank" rel="noopener noreferrer" className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-brand-gold px-5 py-3 font-bold text-brand-navy transition-colors hover:bg-[#D4891A]">
            Open in TidyCal <SafeIcon icon={FiExternalLink} />
          </a>
        </div>
      </div>
    </motion.div>
  );
}

export default function BookingPage() {
  const [activeService, setActiveService] = useState(services[0].id);
  const selectedService = services.find((service) => service.id === activeService) || services[0];
  useEffect(() => {
    applyPageSEO({
      title: 'Book Driving Lessons in Goulburn | Drive Pal Driving School',
      description: 'Book a one-hour automatic driving lesson, pre-test preparation session or test-day car hire package with Drive Pal in Goulburn NSW.',
      keywords: 'book driving lessons Goulburn, automatic driving lesson Goulburn, pre-test preparation Goulburn, test day car hire Goulburn',
      url: 'https://drivepal.au/#/booking',
      ogTitle: 'Book a Driving Service | Drive Pal Goulburn',
      ogDescription: 'Choose and book driving lessons, pre-test preparation or test-day car hire in Goulburn NSW.'
    });
  }, []);
  return (
    <div className="min-h-screen bg-brand-darkbg pt-20">
      <section className="bg-brand-green px-4 py-20">
        <div className="container mx-auto max-w-4xl text-center">
          <motion.h1 initial={{opacity: 0, y: -20}} animate={{opacity: 1, y: 0}} className="mb-6 font-display text-4xl text-white drop-shadow-lg md:text-6xl">
            Book Your <span className="inline-block -rotate-2 rounded-xl bg-brand-gold px-4 py-1 text-brand-navy">Session</span>
          </motion.h1>
          <motion.p initial={{opacity: 0, y: 20}} animate={{opacity: 1, y: 0}} transition={{delay: 0.15}} className="mx-auto max-w-2xl text-xl font-medium text-white md:text-2xl">Choose your service, then select a suitable time from its booking calendar.</motion.p>
        </div>
      </section>
      <section className="px-4 py-16">
        <div className="container mx-auto">
          <ServiceTabs activeService={activeService} onChange={setActiveService} />
          <BookingPanel service={selectedService} />
          <div className="mx-auto mt-8 max-w-4xl rounded-2xl border border-white/10 bg-brand-navy/60 p-5 text-center">
            <div className="mb-3 flex items-center justify-center gap-2 text-brand-gold"><SafeIcon icon={FiCalendar} /><p className="font-bold">Cannot see a suitable calendar time?</p></div>
            <p className="mb-5 text-sm leading-relaxed text-white/75">Call or text Drive Pal to ask about availability.</p>
            <a href="tel:0426501299" className="inline-flex items-center justify-center gap-2 rounded-full border border-brand-gold/40 px-5 py-3 font-bold text-brand-gold transition-colors hover:bg-brand-gold hover:text-brand-navy"><SafeIcon icon={FiPhone} /> Santosh: 0426 501 299</a>
          </div>
          <p className="mx-auto mt-8 max-w-3xl text-center text-xs leading-relaxed text-white/50 sm:text-sm">Drive Pal is independent and is not affiliated with or endorsed by Service NSW or Transport for NSW. Service availability and vehicle requirements are subject to confirmation.</p>
        </div>
      </section>
    </div>
  );
}